import { SealLogo } from "./StampHeader";

// Empty / loading / error states. All three share a layout: centered seal
// over a parchment background with a short headline + supporting copy.

export function LoadingState() {
  return (
    <CenterStack>
      <SealLogo className="w-16 h-16 opacity-90" />
      <p className="text-textSecondary text-sm animate-pulse">Loading story…</p>
    </CenterStack>
  );
}

export function MissingState({ code }: { code: string }) {
  return (
    <CenterStack>
      <SealLogo className="w-16 h-16 opacity-60" />
      <h1 className="text-xl font-semibold text-textPrimary">
        Stamp not recognized
      </h1>
      <p className="text-textSecondary text-sm max-w-xs">
        We couldn't find a stamp with the code{" "}
        <code className="font-mono text-textPrimary">{code}</code>. Make sure
        you scanned the QR code on a real Story Stamp sticker.
      </p>
      <LearnMoreLink />
    </CenterStack>
  );
}

export function UnassignedState() {
  return (
    <CenterStack>
      <SealLogo className="w-16 h-16 opacity-90" />
      <h1 className="text-xl font-semibold text-textPrimary">
        No story attached yet
      </h1>
      <p className="text-textSecondary text-sm max-w-xs">
        This stamp is registered but the owner hasn't written its story yet.
        Check back soon — or if it's yours, open the Story Stamp app to start.
      </p>
      <LearnMoreLink />
    </CenterStack>
  );
}

export function DraftState() {
  return (
    <CenterStack>
      <SealLogo className="w-16 h-16 opacity-90" />
      <h1 className="text-xl font-semibold text-textPrimary">
        Story still being written
      </h1>
      <p className="text-textSecondary text-sm max-w-xs">
        The owner started this story but hasn't published it yet. Try again in
        a few days — drafts go live as soon as they're finished.
      </p>
      <LearnMoreLink />
    </CenterStack>
  );
}

export function PrivateState() {
  return (
    <CenterStack>
      <SealLogo className="w-16 h-16 opacity-90" />
      <h1 className="text-xl font-semibold text-textPrimary">
        This story is private
      </h1>
      <p className="text-textSecondary text-sm max-w-xs">
        The owner has chosen not to share this story publicly. If you know them,
        ask them to add you to their Circle — the private group of people they've
        invited to see their stories.
      </p>
      <LearnMoreLink />
    </CenterStack>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <CenterStack>
      <SealLogo className="w-16 h-16 opacity-60" />
      <h1 className="text-xl font-semibold text-textPrimary">
        Couldn't load this story
      </h1>
      <p className="text-textSecondary text-sm max-w-xs">{message}</p>
      <button
        onClick={() => window.location.reload()}
        className="mt-2 px-5 py-2 rounded-full bg-primary text-white text-sm font-medium"
      >
        Try Again
      </button>
    </CenterStack>
  );
}

function CenterStack({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
      {children}
    </div>
  );
}

function LearnMoreLink() {
  return (
    <a
      href="https://storystamp.com"
      className="text-primary text-sm font-medium underline-offset-2 hover:underline"
    >
      Learn about Story Stamp
    </a>
  );
}
